import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { vendorQueries, aiGuardQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, topBtn } from './VendorDetails';

interface LibraryQuestionnaire {
  id: string;
  name: string;
  description: string | null;
  item_count: number;
  is_active: boolean;
}

interface TemplateItem {
  id: string;
  section: string | null;
  question_text: string;
}

interface TemplateDetail {
  items: TemplateItem[];
  section_titles: Record<string, string> | null;
}

interface SectionGroup {
  key: string;
  title: string | null;
  items: TemplateItem[];
}

type Status = 'sent' | 'opened' | 'answered' | 'reviewed';

interface QuestionnaireResponse {
  id: string;
  questionnaire_id: string;
  questionnaire_name: string;
  recipient_name: string | null;
  recipient_email: string;
  due_date: string | null;
  status: Status;
  sent_at: string;
  opened_at?: string | null;
  answered_at?: string | null;
  reviewed_at?: string | null;
  answered_count: number;
  total_items: number;
  percent_complete: number;
  message?: string | null;
  ai_overview?: string | null;
  ai_overview_generated_at?: string | null;
}

interface Contact {
  id: string;
  name: string | null;
  email: string | null;
  role?: string | null;
  is_primary: boolean;
}

interface EvidenceDoc {
  id: string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  document_type: string | null;
  created_at: string;
}

interface Comment {
  id: string;
  author_type: 'tenant' | 'vendor';
  author_name: string | null;
  body: string;
  created_at: string;
}

interface EvidenceVerification {
  verdict: 'matches' | 'partial' | 'contradicts' | 'insufficient';
  confidence: number | null;
  rationale: string | null;
}

interface AnswerDisagreement {
  severity: 'high' | 'medium' | 'low';
  reason: string;
  verdict: EvidenceVerification['verdict'];
  confidence: number | null;
}

interface AnswerQuestion {
  id: string;
  question_key?: string | null;
  question_text: string;
  question_type: string | null;
  section: string | null;
  answer_value: string | null;
  note?: string | null;
  ai_suggested?: boolean;
  answered_at?: string | null;
  evidence?: EvidenceDoc[];
  comments?: Comment[];
  verification?: EvidenceVerification | null;
  // The vendor's answer vs. the evidence-AI verdict, cross-checked server-side (backend
  // services/questionnaire_signals.py) -- null unless the vendor claimed something is in place
  // AND the evidence check didn't back that up. Never set for a self-disclosed "No"/"N/A" answer.
  disagreement?: AnswerDisagreement | null;
}

const VERIFICATION_META: Record<EvidenceVerification['verdict'], { label: string; icon: string; color: string }> = {
  matches: { label: 'AI check: evidence supports this answer', icon: '✓', color: '#16A34A' },
  partial: { label: 'AI check: evidence is only partially related', icon: '~', color: '#D97706' },
  contradicts: { label: 'AI check: evidence conflicts with this answer', icon: '⚠', color: '#DC2626' },
  insufficient: { label: "AI check: evidence doesn't address this question", icon: '?', color: '#64748B' },
};

const DISAGREEMENT_META: Record<AnswerDisagreement['severity'], { label: string; color: string }> = {
  high: { label: "Doesn't match evidence", color: '#DC2626' },
  medium: { label: "Doesn't match evidence", color: '#D97706' },
  low: { label: "Doesn't match evidence", color: '#A16207' },
};

const fmtSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// A plain <a href> can't carry the tenant's Bearer token, so the download goes through the
// authenticated axios instance and is handed to the browser as a blob instead.
const downloadEvidence = async (vendorId: string, doc: EvidenceDoc) => {
  const res = await api.get(`/v1/vendors/${vendorId}/evidence-documents/${doc.id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = doc.file_name; a.click();
  URL.revokeObjectURL(url);
};

interface Props {
  vendorId: string;
  isDark: boolean;
}

type StatusFilter = 'all' | 'awaiting' | 'answered' | 'reviewed' | 'overdue';

const mono = "'JetBrains Mono', ui-monospace, monospace";
const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const NO_RESPONSES: QuestionnaireResponse[] = [];
const NO_QUESTIONS: AnswerQuestion[] = [];

const STAGES: { key: Status; label: string }[] = [
  { key: 'sent', label: 'Sent' }, { key: 'opened', label: 'Opened' },
  { key: 'answered', label: 'Answered' }, { key: 'reviewed', label: 'Reviewed' },
];
const stageIndex = (status: string) => Math.max(0, STAGES.findIndex(s => s.key === status));

const STATUS_META: Record<Status, { label: string; color: string }> = {
  sent: { label: 'Awaiting response', color: '#64748B' },
  opened: { label: 'In progress', color: '#3B82F6' },
  answered: { label: 'Needs review', color: '#D97706' },
  reviewed: { label: 'Reviewed', color: '#16A34A' },
};

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const parseDate = (value: string) => {
  // due_date is a plain DATE ("2026-10-14"); parse it as local midnight, not UTC
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
};
const daysUntil = (value: string) => Math.round((parseDate(value).getTime() - startOfToday().getTime()) / 86400000);
const isOverdue = (r: QuestionnaireResponse) => !!r.due_date && (r.status === 'sent' || r.status === 'opened') && daysUntil(r.due_date) < 0;

const fmtDate = (value: string | null | undefined) =>
  value ? parseDate(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtRelative = (value: string) => {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return fmtDate(value);
};
const isoInDays = (days: number) => {
  const d = startOfToday();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PALETTE = ['#3B82F6', '#16A34A', '#D97706', '#7C3AED', '#DB2777', '#0D9488', '#4F46E5', '#059669'];
const hashColor = (text: string) => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};
const initials = (name: string) => name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';

/* ── icons ─────────────────────────────────────────────────────────── */
const Icon = ({ d, size = 14 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const SEND = 'M22 2 11 13M22 2 15 22l-4-9-9-4 20-7Z';
const DOWNLOAD = 'M12 4v12M6 12l6 6 6-6M4 20h16';
const SEARCH = 'M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14ZM21 21l-4.3-4.3';
const CLOSE = 'M6 6l12 12M18 6 6 18';
const CHEVRON = 'M9 6l6 6-6 6';
const DOC = 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM14 3v5h5M9 13h6M9 17h4';
const CHECK = 'M5 12l5 5L20 7';

function Avatar({ name, size = 30 }: { name: string; size?: number }) {
  const color = hashColor(name);
  return (
    <span style={{
      width: size, height: size, borderRadius: 99, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}1f`, color, fontSize: size * 0.38, fontWeight: 700,
    }}>{initials(name)}</span>
  );
}

function Stepper({ status, isDark, dates, large = false }: {
  status: Status; isDark: boolean; dates?: (string | null | undefined)[]; large?: boolean;
}) {
  const idx = stageIndex(status);
  const accent = isDark ? '#3B82F6' : T.blue;
  const done = '#16A34A';
  const line = isDark ? '#262626' : T.bdr;
  const muted = isDark ? '#94a3b8' : T.muted;
  const dotSize = large ? 22 : 9;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {STAGES.map((stage, i) => {
        const reached = i <= idx;
        const color = idx === 3 ? done : accent;
        return (
          <div key={stage.key} style={{ display: 'flex', alignItems: 'flex-start', flex: i < STAGES.length - 1 ? 1 : undefined }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: large ? 'center' : 'flex-start', gap: large ? 6 : 0, minWidth: large ? 70 : undefined }}>
              <span style={{
                width: dotSize, height: dotSize, borderRadius: 99, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: reached ? color : (isDark ? '#0d0d0d' : '#fff'), border: `2px solid ${reached ? color : line}`, color: '#fff',
              }}>
                {large && reached && <Icon d={CHECK} size={12} />}
              </span>
              {large && (
                <>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: reached ? (isDark ? '#e5e7eb' : T.ink) : muted }}>{stage.label}</span>
                  <span style={{ fontSize: 11, color: muted, whiteSpace: 'nowrap' }}>{dates?.[i] ? fmtDate(dates[i]) : '—'}</span>
                </>
              )}
            </div>
            {i < STAGES.length - 1 && (
              <div style={{ flex: 1, height: 2, borderRadius: 2, background: i < idx ? color : line, margin: large ? '10px 4px 0' : '3.5px 3px 0', minWidth: large ? 16 : 10 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

const answerTone = (value: string | null): string | null => {
  if (!value) return null;
  const v = value.trim().toLowerCase().replace(/[.!\s]+$/, '');
  if (['yes', 'true', 'implemented', 'compliant'].includes(v)) return '#16A34A';
  if (['no', 'false', 'not implemented', 'non-compliant'].includes(v)) return '#DC2626';
  if (['n/a', 'na', 'not applicable', 'partial', 'partially', 'partially implemented'].includes(v)) return '#D97706';
  return null;
};

export default function VendorQuestionnaires({ vendorId, isDark }: Props) {
  const { user } = useAuth();
  const canSend = !!user && user.role !== 'viewer';

  const [sendOpen, setSendOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const accent = isDark ? '#3B82F6' : T.blue;

  const responsesQuery = useQuery({
    queryKey: ['questionnaire-responses', 'vendor', vendorId],
    queryFn: async () => ((await api.get(`/v1/questionnaire-responses/vendor/${vendorId}`)).data.responses || []) as QuestionnaireResponse[],
  });
  const responses = responsesQuery.data ?? NO_RESPONSES;
  const loading = !responsesQuery.data && (responsesQuery.isPending || responsesQuery.isFetching);
  const loadError = !responsesQuery.data && responsesQuery.isError
    ? apiError(responsesQuery.error, 'Could not load this vendor\'s questionnaires.') : '';

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const counts = useMemo(() => ({
    all: responses.length,
    awaiting: responses.filter(r => r.status === 'sent' || r.status === 'opened').length,
    answered: responses.filter(r => r.status === 'answered').length,
    reviewed: responses.filter(r => r.status === 'reviewed').length,
    overdue: responses.filter(isOverdue).length,
  }), [responses]);

  const avgCompletion = responses.length
    ? Math.round(responses.reduce((sum, r) => sum + (r.percent_complete || 0), 0) / responses.length) : 0;

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return responses.filter(r => {
      if (filter === 'awaiting' && !(r.status === 'sent' || r.status === 'opened')) return false;
      if (filter === 'answered' && r.status !== 'answered') return false;
      if (filter === 'reviewed' && r.status !== 'reviewed') return false;
      if (filter === 'overdue' && !isOverdue(r)) return false;
      if (!needle) return true;
      return [r.questionnaire_name, r.recipient_name, r.recipient_email].some(v => (v || '').toLowerCase().includes(needle));
    });
  }, [responses, search, filter]);

  const viewing = responses.find(r => r.id === viewingId) || null;

  const exportCsv = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = ['Questionnaire', 'Recipient name', 'Recipient email', 'Status', 'Sent', 'Due date', 'Answered', 'Total questions', '% complete'];
    const body = shown.map(r => [
      r.questionnaire_name, r.recipient_name || '', r.recipient_email, STATUS_META[r.status]?.label || r.status,
      r.sent_at ? new Date(r.sent_at).toISOString().slice(0, 10) : '', r.due_date || '',
      String(r.answered_count), String(r.total_items), String(r.percent_complete),
    ].map(escape).join(','));
    const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `questionnaires-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── styles ─────────────────────────────────────────────────────── */
  const tag = (color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
    whiteSpace: 'nowrap', color, background: `${color}${isDark ? '22' : '14'}`, border: `1px solid ${color}40`,
  });
  const dot = (color: string): React.CSSProperties => ({ width: 6, height: 6, borderRadius: 99, background: color, flexShrink: 0 });
  const headCell: React.CSSProperties = {
    padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
    color: slate, background: surface2, borderBottom: `1px solid ${line}`, whiteSpace: 'nowrap',
  };
  const cell: React.CSSProperties = { padding: '16px', fontSize: 14, color: ink, borderBottom: `1px solid ${isDark ? '#1a1a1a' : '#EEF2F6'}`, verticalAlign: 'middle' };
  const segment = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, fontWeight: 600, fontFamily: sans,
    cursor: 'pointer', border: 'none', borderRadius: 6, whiteSpace: 'nowrap',
    background: active ? surface : 'transparent', color: active ? ink : slate,
    boxShadow: active ? (isDark ? '0 0 0 1px #262626' : '0 1px 2px rgba(15,23,42,.08), 0 0 0 1px #E2E8F0') : 'none',
  });
  const countBadge = (active: boolean, tone?: string): React.CSSProperties => ({
    fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 99, fontVariantNumeric: 'tabular-nums',
    background: tone ? `${tone}22` : (active ? (isDark ? '#1f2937' : T.surface2) : (isDark ? '#1a1a1a' : '#EEF2F6')),
    color: tone || slate,
  });

  const dueCell = (r: QuestionnaireResponse) => {
    if (!r.due_date) return <span style={{ color: muted }}>No due date</span>;
    const days = daysUntil(r.due_date);
    const open = r.status === 'sent' || r.status === 'opened';
    let hint: React.ReactNode = null;
    if (open && days < 0) hint = <span style={tag('#DC2626')}>Overdue {Math.abs(days)}d</span>;
    else if (open && days <= 3) hint = <span style={tag('#D97706')}>{days === 0 ? 'Due today' : `Due in ${days}d`}</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5 }}>
        <span>{fmtDate(r.due_date)}</span>
        {hint}
      </div>
    );
  };

  const kpis: { label: string; value: string | number; note: string; tone?: string; filter?: StatusFilter }[] = [
    { label: 'Sent', value: counts.all, note: responses[0] ? `Last sent ${fmtRelative(responses[0].sent_at)}` : 'Nothing sent yet', filter: 'all' },
    { label: 'Awaiting response', value: counts.awaiting, note: 'Sent or in progress', filter: 'awaiting' },
    { label: 'Needs review', value: counts.answered, note: counts.answered ? 'Answered, not yet reviewed' : 'Nothing waiting on you', tone: counts.answered ? '#D97706' : undefined, filter: 'answered' },
    { label: 'Overdue', value: counts.overdue, note: counts.overdue ? 'Past due, not answered' : 'All on schedule', tone: counts.overdue ? '#DC2626' : '#16A34A', filter: 'overdue' },
    { label: 'Average completion', value: `${avgCompletion}%`, note: `${counts.reviewed} reviewed` },
  ];

  return (
    <div style={{ fontFamily: sans }}>
      <style>{'@keyframes qSlideIn { from { transform: translateX(100%) } to { transform: translateX(0) } } @keyframes qSlideOut { from { transform: translateX(0) } to { transform: translateX(100%) } } @keyframes qShimmer { 0%,100% { opacity: .55 } 50% { opacity: 1 } } @keyframes qPop { from { transform: translateY(6px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } }'}</style>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: ink, letterSpacing: '-.01em' }}>Questionnaires</div>
          <div style={{ fontSize: 13.5, color: slate, marginTop: 4, maxWidth: 640, lineHeight: 1.5 }}>
            Ask this vendor directly about the controls a scan can't see. Send a template from your Questionnaire Library,
            then track every response from sent to reviewed.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={exportCsv} disabled={!shown.length} style={{ ...topBtn(false, isDark), opacity: shown.length ? 1 : 0.5, cursor: shown.length ? 'pointer' : 'not-allowed' }}>
            <Icon d={DOWNLOAD} /> Export CSV
          </button>
          {canSend && (
            <button style={topBtn(true, isDark)} onClick={() => setSendOpen(true)}><Icon d={SEND} /> Send questionnaire</button>
          )}
        </div>
      </div>

      {notice && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: isDark ? '#86efac' : T.grn,
          background: isDark ? '#052e16' : T.grnBg, border: `1px solid ${isDark ? '#14532d' : T.grnBdr}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14,
        }}>
          <Icon d={CHECK} /> {notice}
        </div>
      )}

      {/* ── KPI strip ───────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 14 }}>
        {kpis.map(k => {
          const active = !!k.filter && k.filter !== 'all' && filter === k.filter;
          return (
            <div
              key={k.label} role={k.filter ? 'button' : undefined}
              onClick={() => k.filter && setFilter(filter === k.filter ? 'all' : k.filter)}
              style={{
                ...card(isDark), padding: '16px 18px', cursor: k.filter ? 'pointer' : 'default',
                borderColor: active ? accent : undefined, boxShadow: active ? `0 0 0 1px ${accent}` : undefined,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: slate, marginBottom: 8 }}>{k.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: k.tone || ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {loading ? '–' : k.value}
              </div>
              <div style={{ fontSize: 12, color: muted, marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.note}</div>
            </div>
          );
        })}
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: muted, display: 'flex' }}><Icon d={SEARCH} /></span>
            <input
              value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questionnaire or recipient…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px 8px 32px', fontSize: 13, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 8, outline: 'none' }}
            />
          </div>
          <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 8, background: surface2, border: `1px solid ${line}`, overflowX: 'auto' }}>
            {([
              ['all', 'All', undefined], ['awaiting', 'Awaiting', undefined], ['answered', 'Needs review', counts.answered ? '#D97706' : undefined],
              ['reviewed', 'Reviewed', undefined], ['overdue', 'Overdue', counts.overdue ? '#DC2626' : undefined],
            ] as [StatusFilter, string, string | undefined][]).map(([key, label, tone]) => (
              <button key={key} style={segment(filter === key)} onClick={() => setFilter(key)}>
                {label}<span style={countBadge(filter === key, tone)}>{counts[key]}</span>
              </button>
            ))}
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: slate, fontVariantNumeric: 'tabular-nums' }}>
            {shown.length === responses.length ? `${responses.length} questionnaire${responses.length === 1 ? '' : 's'}` : `${shown.length} of ${responses.length}`}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2].map(i => <div key={i} style={{ height: 58, borderRadius: 8, background: surface2, animation: 'qShimmer 1.2s infinite' }} />)}
          </div>
        ) : loadError ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 6 }}>Couldn't load questionnaires</div>
            <div style={{ fontSize: 13, color: slate, marginBottom: 14 }}>{loadError}</div>
            <button onClick={() => responsesQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
          </div>
        ) : shown.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: isDark ? '#172554' : T.blueBg, color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <Icon d={DOC} size={22} />
            </div>
            <div style={{ fontSize: 15.5, fontWeight: 600, color: ink, marginBottom: 6 }}>
              {responses.length ? 'Nothing matches this view' : 'No questionnaires sent to this vendor yet'}
            </div>
            <div style={{ fontSize: 13.5, color: slate, maxWidth: 420, margin: '0 auto 16px', lineHeight: 1.5 }}>
              {responses.length
                ? 'Try another status or clear the search.'
                : 'Send a security questionnaire to collect answers about policies and controls that outside scanning can\'t observe.'}
            </div>
            {responses.length ? (
              <button onClick={() => { setFilter('all'); setSearch(''); }} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Clear filters</button>
            ) : canSend && (
              <button onClick={() => setSendOpen(true)} style={{ ...topBtn(true, isDark), margin: '0 auto' }}><Icon d={SEND} /> Send questionnaire</button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
              <thead>
                <tr>
                  <th style={headCell}>Questionnaire</th>
                  <th style={headCell}>Recipient</th>
                  <th style={headCell}>Sent</th>
                  <th style={headCell}>Due</th>
                  <th style={{ ...headCell, width: 190 }}>Progress</th>
                  <th style={{ ...headCell, width: 170 }}>Completion</th>
                  <th style={{ ...headCell, width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {shown.map(r => {
                  const meta = STATUS_META[r.status] || STATUS_META.sent;
                  const active = r.id === viewingId;
                  const barColor = r.status === 'reviewed' ? '#16A34A' : r.percent_complete === 100 ? '#D97706' : accent;
                  return (
                    <tr
                      key={r.id} onClick={() => setViewingId(r.id)}
                      style={{ cursor: 'pointer', background: active ? (isDark ? '#111827' : T.blueBg) : 'transparent', transition: 'background .12s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = surface2; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ ...cell, boxShadow: active ? `inset 3px 0 0 ${accent}` : undefined }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#172554' : T.blueBg, color: accent }}>
                            <Icon d={DOC} size={17} />
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: ink, whiteSpace: 'nowrap' }}>{r.questionnaire_name}</div>
                            <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>{r.total_items} question{r.total_items === 1 ? '' : 's'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={cell}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={r.recipient_name || r.recipient_email} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{r.recipient_name || r.recipient_email.split('@')[0]}</div>
                            <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>{r.recipient_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <div>{fmtDate(r.sent_at)}</div>
                        <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>{fmtRelative(r.sent_at)}</div>
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>{dueCell(r)}</td>
                      <td style={cell}>
                        <div style={{ marginBottom: 8 }}><span style={tag(meta.color)}><span style={dot(meta.color)} />{meta.label}</span></div>
                        <Stepper status={r.status} isDark={isDark} />
                      </td>
                      <td style={cell}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{r.percent_complete}%</span>
                          <span style={{ color: muted, fontVariantNumeric: 'tabular-nums' }}>{r.answered_count}/{r.total_items}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 99, background: isDark ? '#1f1f1f' : '#EEF2F6', overflow: 'hidden' }}>
                          <div style={{ width: `${r.percent_complete}%`, height: '100%', borderRadius: 99, background: barColor, transition: 'width .3s' }} />
                        </div>
                      </td>
                      <td style={{ ...cell, color: muted, textAlign: 'right', paddingRight: 18 }}><Icon d={CHEVRON} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sendOpen && (
        <SendQuestionnaireModal
          vendorId={vendorId} isDark={isDark}
          onClose={() => setSendOpen(false)}
          onSent={(name, sentTo) => {
            setSendOpen(false);
            setNotice(sentTo.length === 1 ? `"${name}" sent to ${sentTo[0]}.` : `"${name}" sent to ${sentTo.length} recipients.`);
          }}
        />
      )}
      {viewing && (
        <ResponseDrawer
          row={viewing} isDark={isDark} canReview={canSend} vendorId={vendorId}
          onClose={() => setViewingId(null)}
          onReviewed={() => setNotice(`"${viewing.questionnaire_name}" marked as reviewed.`)}
        />
      )}
    </div>
  );
}

/* ── Send questionnaire: 4 steps (template, due date & note, recipients, review) ── */
interface NewRecipient { key: number; name: string; email: string; save: boolean }

function SendQuestionnaireModal({ vendorId, isDark, onClose, onSent }: {
  vendorId: string; isDark: boolean; onClose: () => void; onSent: (name: string, recipients: string[]) => void;
}) {
  const [step, setStep] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState('');
  // 'default' = let the backend apply its own default interval; 'none' = no reminder at all;
  // otherwise a repeat interval in days (e.g. '7' = a reminder every 7 days until answered or due).
  // Only meaningful when a due date is actually set.
  const [reminderDays, setReminderDays] = useState<'default' | 'none' | '1' | '3' | '7' | '14'>('default');
  const [message, setMessage] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [newPeople, setNewPeople] = useState<NewRecipient[]>([]);
  const [nextKey, setNextKey] = useState(1);
  const [sendError, setSendError] = useState('');
  const recipientsInitialised = useRef(false);
  const sectionsInitialisedFor = useRef('');

  const libraryQuery = useQuery({
    queryKey: ['questionnaire-library', 'list', 'published'],
    queryFn: async () => ((await api.get('/v1/questionnaire-library/')).data.questionnaires || [])
      .filter((q: LibraryQuestionnaire) => q.is_active) as LibraryQuestionnaire[],
  });
  const questionnaires = libraryQuery.data ?? [];
  const loading = libraryQuery.isPending;
  const loadError = libraryQuery.isError ? apiError(libraryQuery.error, 'Could not load the questionnaire library.') : '';

  // The chosen template's own items, fetched once a template is picked -- only to derive its
  // section list for the "which sections should this vendor answer" step. Selection is deliberately
  // section-level only (per explicit user instruction): checking a section sends every question in
  // it, unchecking drops every question in it -- no per-question picking.
  const templateQuery = useQuery({
    queryKey: ['questionnaire-library', 'detail', selectedId],
    queryFn: async () => {
      const data = (await api.get(`/v1/questionnaire-library/${selectedId}`)).data;
      return { items: data.items || [], section_titles: data.section_titles || null } as TemplateDetail;
    },
    enabled: !!selectedId,
  });
  const templateItems = useMemo(() => templateQuery.data?.items ?? [], [templateQuery.data]);
  const templateItemsLoading = !!selectedId && templateQuery.isPending;

  // Sections in the template's own order, each with its display title (when the template has one)
  // and its questions -- the questions power the per-section preview.
  const sectionGroups = useMemo<SectionGroup[]>(() => {
    const titles = templateQuery.data?.section_titles || {};
    const groups = new Map<string, TemplateItem[]>();
    templateItems.forEach(it => {
      const key = it.section || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(it);
    });
    return Array.from(groups.entries()).map(([key, items]) => ({ key, title: titles[key] || null, items }));
  }, [templateItems, templateQuery.data]);
  const [previewSection, setPreviewSection] = useState<string | null>(null);

  // Default every section to selected the first time a template's items finish loading -- and
  // again whenever the sender picks a different template, since that's a fresh send.
  useEffect(() => {
    if (!selectedId || templateItemsLoading || sectionsInitialisedFor.current === selectedId) return;
    sectionsInitialisedFor.current = selectedId;
    setSelectedSections(new Set(sectionGroups.map(g => g.key)));
    setPreviewSection(null);
  }, [selectedId, templateItemsLoading, sectionGroups]);

  const toggleSection = (key: string) => setSelectedSections(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const selectedQuestionCount = templateItems.filter(it => selectedSections.has(it.section || '')).length;

  // Sending is refused server-side without a verified-working AI provider (backend/middleware/
  // ai_guard.py); this is purely so the final Send button can say why up front. The server
  // re-checks independently either way.
  const aiStatus = useQuery(aiGuardQueries.status());
  const aiBlocked = aiStatus.data?.configured === false;

  const contactsQuery = useQuery(vendorQueries.contacts(vendorId));
  const contacts = useMemo(() => ((contactsQuery.data ?? []) as Contact[]).filter(c => c.email), [contactsQuery.data]);
  const contactsLoaded = !contactsQuery.isPending;

  // Preselect recipients once, when the dialog first gets the contact list -- never again on a refetch, so a
  // background update can't undo the user's picks.
  useEffect(() => {
    if (recipientsInitialised.current || contactsQuery.isPending) return;
    recipientsInitialised.current = true;
    const primary = contacts.find(c => c.is_primary);
    if (contactsQuery.isError && !contactsQuery.data) setNewPeople([{ key: 0, name: '', email: '', save: true }]);
    else if (primary) setPicked(new Set([primary.id]));
    else if (!contacts.length) setNewPeople([{ key: 0, name: '', email: '', save: true }]);
  }, [contacts, contactsQuery.isPending, contactsQuery.isError, contactsQuery.data]);

  const sendMutation = useMutation({
    mutationFn: async (list: { name: string; email: string; isNew: boolean; save: boolean }[]) => {
      const sent: string[] = [];
      const failed: string[] = [];
      // The API sends to one person per call -- each recipient gets their own copy and their own answer link.
      for (const r of list) {
        try {
          await api.post('/v1/questionnaire-responses/', {
            vendor_id: vendorId,
            questionnaire_id: selectedId,
            recipient_name: r.name || null,
            recipient_email: r.email,
            message: message.trim() || null,
            due_date: dueDate || null,
            // Omitted (undefined) when there's no due date or the sender left it on Default --
            // the backend then applies its own default repeat interval. 0 means "no reminder" explicitly.
            reminder_days: !dueDate || reminderDays === 'default' ? undefined : reminderDays === 'none' ? 0 : Number(reminderDays),
            selected_sections: Array.from(selectedSections),
          });
          sent.push(r.email);
          if (r.isNew && r.save && r.name && !knownEmails.has(r.email.toLowerCase())) {
            // Best effort: the questionnaire already went out, so a contact-save failure is not a send failure.
            await api.post(`/v1/vendors/${vendorId}/contacts`, { name: r.name, email: r.email }).catch(() => undefined);
          }
        } catch (err) {
          failed.push(`${r.email} (${apiError(err, 'failed')})`);
        }
      }
      return { sent, failed };
    },
    meta: { invalidates: [['questionnaire-responses'], vendorQueries.contacts(vendorId).queryKey] },
  });
  const sending = sendMutation.isPending;

  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const accent = isDark ? '#3B82F6' : T.blue;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const selectedBg = isDark ? '#0b1730' : T.blueBg;

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, fontFamily: sans, color: ink,
    background: surface, border: `1px solid ${line}`, borderRadius: 8, outline: 'none',
  };
  const fieldLabel: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: ink, marginBottom: 6, display: 'block' };
  const groupLabel: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: slate };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const emailOk = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const knownEmails = new Set(contacts.map(c => (c.email || '').toLowerCase()));

  // Everyone the questionnaire goes to: picked contacts first, then new people with an email typed in.
  const recipients = [
    ...contacts.filter(c => picked.has(c.id)).map(c => ({ name: c.name || '', email: (c.email || '').trim(), isNew: false, save: false })),
    ...newPeople.filter(p => p.email.trim()).map(p => ({ name: p.name.trim(), email: p.email.trim(), isNew: true, save: p.save })),
  ];
  const lowered = recipients.map(r => r.email.toLowerCase());
  const duplicateEmail = lowered.find((e, i) => lowered.indexOf(e) !== i);
  const invalidNew = newPeople.some(p => p.email.trim() && !emailOk(p.email));
  const recipientsOk = recipients.length > 0 && !invalidNew && !duplicateEmail;

  const steps = ['Questionnaire', 'Sections', 'Due date & note', 'Recipients', 'Review'];
  const selected = questionnaires.find(q => q.id === selectedId) || null;
  const canNext = step === 0 ? !!selected : step === 1 ? selectedSections.size > 0 : step === 3 ? recipientsOk : true;
  const filteredTemplates = questionnaires.filter(q =>
    !templateSearch.trim() || q.name.toLowerCase().includes(templateSearch.trim().toLowerCase()));

  const togglePick = (id: string) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const addPerson = () => { setNewPeople(p => [...p, { key: nextKey, name: '', email: '', save: true }]); setNextKey(k => k + 1); };
  const updatePerson = (key: number, patch: Partial<NewRecipient>) => setNewPeople(p => p.map(x => x.key === key ? { ...x, ...patch } : x));
  const removePerson = (key: number) => setNewPeople(p => p.filter(x => x.key !== key));

  const handleSend = async () => {
    setSendError('');
    const { sent, failed } = await sendMutation.mutateAsync(recipients);
    if (!failed.length) {
      onSent(selected?.name || 'Questionnaire', sent);
      return;
    }
    // Drop whoever already got it, so "Send" again only retries the ones that failed.
    const done = new Set(sent.map(e => e.toLowerCase()));
    setPicked(prev => new Set(Array.from(prev).filter(id => !done.has((contacts.find(c => c.id === id)?.email || '').toLowerCase()))));
    setNewPeople(prev => prev.filter(p => !done.has(p.email.trim().toLowerCase())));
    setSendError(sent.length
      ? `Sent to ${sent.join(', ')}. These failed and are still selected -- send again to retry: ${failed.join('; ')}`
      : `Could not send: ${failed.join('; ')}`);
  };

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', fontSize: 13.5, fontWeight: 600, fontFamily: sans, borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? accent : line}`, background: active ? selectedBg : surface, color: active ? accent : ink,
  });
  const checkbox = (on: boolean): React.CSSProperties => ({
    width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: `2px solid ${on ? accent : (isDark ? '#333' : T.faint)}`, background: on ? accent : 'transparent', color: '#fff',
  });

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 24 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 960, height: 'min(820px, 90vh)', display: 'flex', flexDirection: 'column', background: surface,
        border: `1px solid ${line}`, borderRadius: 16, fontFamily: sans, overflow: 'hidden', animation: 'qPop .16s ease-out',
      }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 32px 0' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: ink, letterSpacing: '-.01em' }}>Send a questionnaire</div>
            <div style={{ fontSize: 14, color: slate, marginTop: 4 }}>Step {step + 1} of {steps.length} · {steps[step]}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: surface2, border: `1px solid ${line}`, color: slate, cursor: 'pointer', width: 36, height: 36, borderRadius: 9,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon d={CLOSE} size={16} /></button>
        </div>

        {/* step indicator */}
        <div style={{ display: 'flex', gap: 8, padding: '20px 32px 0' }}>
          {steps.map((label, i) => (
            <div key={label} style={{ flex: 1 }}>
              <div style={{ height: 4, borderRadius: 99, background: i <= step ? accent : (isDark ? '#1f1f1f' : '#E2E8F0'), transition: 'background .2s' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: i === step ? 700 : 500, color: i <= step ? (i === step ? ink : slate) : muted, marginTop: 8 }}>
                {i < step && <span style={{ color: '#16A34A', display: 'flex' }}><Icon d={CHECK} size={13} /></span>}
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* body */}
        <div style={{ padding: '26px 32px', overflowY: 'auto', flex: 1 }}>
          {step === 0 && (
            loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[0, 1, 2].map(i => <div key={i} style={{ height: 80, borderRadius: 12, background: surface2, animation: 'qShimmer 1.2s infinite' }} />)}
              </div>
            ) : loadError ? (
              <div style={{ fontSize: 14, color: '#DC2626' }}>{loadError}</div>
            ) : questionnaires.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 12px', color: slate, fontSize: 14.5, lineHeight: 1.6 }}>
                No questionnaires are published yet. Switch one on in the{' '}
                <a href="/questionnaire-library" target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 600 }}>Questionnaire Library</a> first.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: ink }}>Which questionnaire should this vendor answer?</div>
                  {questionnaires.length > 4 && (
                    <input value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} placeholder="Search templates…" style={{ ...inputStyle, width: 240 }} />
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
                  {filteredTemplates.map(q => {
                    const active = q.id === selectedId;
                    return (
                      <button key={q.id} onClick={() => setSelectedId(q.id)} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 14, width: '100%', textAlign: 'left', padding: '16px 18px', borderRadius: 12,
                        cursor: 'pointer', fontFamily: sans, border: `1.5px solid ${active ? accent : line}`, background: active ? selectedBg : surface,
                      }}>
                        <span style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#172554' : T.blueBg, color: accent }}>
                          <Icon d={DOC} size={19} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: ink }}>{q.name}</span>
                            <span style={{ width: 20, height: 20, borderRadius: 99, flexShrink: 0, border: `2px solid ${active ? accent : (isDark ? '#333' : T.faint)}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {active && <span style={{ width: 10, height: 10, borderRadius: 99, background: accent }} />}
                            </span>
                          </div>
                          {q.description && (
                            <div style={{ fontSize: 13, color: slate, marginTop: 6, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                              {q.description}
                            </div>
                          )}
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: muted, marginTop: 10 }}>{q.item_count} questions</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {filteredTemplates.length === 0 && <div style={{ fontSize: 14, color: slate, padding: '12px 0' }}>No templates match "{templateSearch}".</div>}
                <div style={{ fontSize: 13, color: muted, marginTop: 16 }}>
                  Only published templates appear here. Manage them in the{' '}
                  <a href="/questionnaire-library" target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 600, textDecoration: 'none' }}>Questionnaire Library</a>.
                </div>
              </>
            )
          )}

          {step === 1 && (
            templateItemsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ height: 96, borderRadius: 12, background: surface2, animation: 'qShimmer 1.2s infinite' }} />
                {[0, 1, 2, 3].map(i => <div key={i} style={{ height: 68, borderRadius: 12, background: surface2, animation: 'qShimmer 1.2s infinite' }} />)}
              </div>
            ) : (
              <>
                {/* heading + bulk actions */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: ink }}>Choose the sections to send</div>
                    <div style={{ fontSize: 13.5, color: slate, marginTop: 4, lineHeight: 1.5 }}>
                      <strong style={{ color: ink, fontWeight: 600 }}>{selected?.name}</strong> · each section goes out with every question in it.
                    </div>
                  </div>
                  {sectionGroups.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => setSelectedSections(new Set(sectionGroups.map(g => g.key)))}
                        disabled={selectedSections.size === sectionGroups.length}
                        style={{ ...topBtn(false, isDark), padding: '6px 12px', fontSize: 13, opacity: selectedSections.size === sectionGroups.length ? 0.5 : 1 }}>
                        Select all
                      </button>
                      <button onClick={() => setSelectedSections(new Set())} disabled={selectedSections.size === 0}
                        style={{ ...topBtn(false, isDark), padding: '6px 12px', fontSize: 13, opacity: selectedSections.size === 0 ? 0.5 : 1 }}>
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* summary: how much of the template goes out */}
                <div style={{ border: `1px solid ${line}`, borderRadius: 12, padding: '16px 18px', marginBottom: 16, background: surface }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 28, fontWeight: 800, color: selectedSections.size ? ink : '#DC2626', lineHeight: 1, letterSpacing: '-.02em' }}>{selectedQuestionCount}</span>
                      <span style={{ fontSize: 14, color: slate }}>of {templateItems.length} questions will be sent</span>
                    </div>
                    <span style={{
                      fontSize: 12.5, fontWeight: 700, borderRadius: 99, padding: '4px 11px',
                      color: selectedSections.size ? accent : '#DC2626',
                      background: selectedSections.size ? (isDark ? '#172554' : T.blueBg) : (isDark ? '#3f1d1d' : '#FEE2E2'),
                    }}>
                      {selectedSections.size} of {sectionGroups.length} section{sectionGroups.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 3, marginTop: 14 }}>
                    {sectionGroups.map(g => {
                      const label = g.title || (g.key ? `Section ${g.key}` : 'General');
                      return (
                        <div key={g.key || '__none__'} title={`${label} · ${g.items.length} questions`}
                          style={{
                            flex: g.items.length, minWidth: 6, height: 8, borderRadius: 99, transition: 'background .15s',
                            background: selectedSections.has(g.key) ? accent : (isDark ? '#262626' : '#E2E8F0'),
                          }} />
                      );
                    })}
                  </div>
                </div>

                {/* one card per section */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sectionGroups.map(g => {
                    const on = selectedSections.has(g.key);
                    const open = previewSection === g.key;
                    const n = g.items.length;
                    const title = g.title || (g.key ? `Section ${g.key}` : 'General');
                    const subtitle = g.title
                      ? `Section ${g.key}`
                      : (g.items[0]?.question_text ? `Starts with: ${g.items[0].question_text}` : '');
                    return (
                      <div key={g.key || '__none__'} style={{
                        borderRadius: 12, background: surface, overflow: 'hidden', transition: 'border-color .15s',
                        border: `1.5px solid ${on ? accent : line}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button onClick={() => toggleSection(g.key)} aria-pressed={on} style={{
                            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', textAlign: 'left',
                            background: 'none', border: 'none', cursor: 'pointer', fontFamily: sans,
                          }}>
                            <span style={checkbox(on)}>{on && <Icon d={CHECK} size={12} />}</span>
                            <span style={{
                              width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 13.5, fontWeight: 800, transition: 'all .15s',
                              color: on ? accent : muted, background: on ? (isDark ? '#172554' : T.blueBg) : surface2,
                            }}>
                              {g.key && g.key.length <= 3 ? g.key : title.charAt(0).toUpperCase()}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 15, fontWeight: 650, color: on ? ink : slate }}>{title}</span>
                              {subtitle && (
                                <span style={{ display: 'block', fontSize: 12.5, color: muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {subtitle}
                                </span>
                              )}
                            </span>
                            <span style={{
                              fontSize: 12, fontWeight: 700, borderRadius: 99, padding: '4px 10px', flexShrink: 0, whiteSpace: 'nowrap',
                              color: on ? accent : muted, background: on ? (isDark ? '#172554' : T.blueBg) : surface2,
                            }}>
                              {on ? `${n} question${n === 1 ? '' : 's'}` : 'Not sent'}
                            </span>
                          </button>
                          <button onClick={() => setPreviewSection(open ? null : g.key)} aria-expanded={open}
                            title={open ? 'Hide questions' : 'Preview questions'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'stretch', padding: '0 16px', flexShrink: 0,
                              background: 'none', borderWidth: '0 0 0 1px', borderStyle: 'solid', borderColor: line, color: slate, cursor: 'pointer',
                              fontFamily: sans, fontSize: 12.5, fontWeight: 600,
                            }}>
                            {open ? 'Hide' : 'Preview'}
                            <span style={{ display: 'inline-flex', transform: `rotate(${open ? 90 : 0}deg)`, transition: 'transform .15s' }}>
                              <Icon d={CHEVRON} size={14} />
                            </span>
                          </button>
                        </div>
                        {open && (
                          <div style={{ borderTop: `1px solid ${line}`, background: surface2, maxHeight: 280, overflowY: 'auto' }}>
                            {g.items.map((it, idx) => (
                              <div key={it.id} style={{ padding: '10px 18px 10px 66px', borderTop: idx ? `1px solid ${line}` : 'none' }}>
                                <div style={{
                                  fontSize: 13, color: slate, lineHeight: 1.5,
                                  overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                }}>
                                  {it.question_text}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedSections.size === 0 && (
                  <div style={{ fontSize: 13, color: '#DC2626', marginTop: 14 }}>Pick at least one section to continue.</div>
                )}
              </>
            )
          )}

          {step === 2 && (
            <div style={{ maxWidth: 680 }}>
              <label style={{ ...fieldLabel, fontSize: 15 }}>Due date <span style={{ color: muted, fontWeight: 500 }}>(optional)</span></label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {[7, 14, 30].map(days => (
                  <button key={days} onClick={() => setDueDate(isoInDays(days))} style={chip(dueDate === isoInDays(days))}>In {days} days</button>
                ))}
                <button onClick={() => setDueDate('')} style={chip(!dueDate)}>No due date</button>
              </div>
              <input type="date" value={dueDate} min={isoInDays(0)} onChange={e => setDueDate(e.target.value)} style={{ ...inputStyle, maxWidth: 240, marginBottom: dueDate ? 20 : 28 }} />

              {dueDate && (
                <>
                  <label style={{ ...fieldLabel, fontSize: 15 }}>Send a reminder every <span style={{ color: muted, fontWeight: 500 }}>(repeats until answered or the due date, whichever comes first)</span></label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
                    {([
                      ['default', 'Default (every 3 days)'], ['1', 'Every 1 day'], ['3', 'Every 3 days'],
                      ['7', 'Every 7 days'], ['14', 'Every 14 days'], ['none', 'No reminder'],
                    ] as const).map(([key, label]) => (
                      <button key={key} onClick={() => setReminderDays(key)} style={chip(reminderDays === key)}>{label}</button>
                    ))}
                  </div>
                </>
              )}

              <label style={{ ...fieldLabel, fontSize: 15 }}>Note to the recipient <span style={{ color: muted, fontWeight: 500 }}>(optional)</span></label>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)} rows={7} maxLength={2000}
                placeholder="e.g. As part of our annual vendor review, please complete this questionnaire by the due date."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              />
              <div style={{ fontSize: 12, color: muted, textAlign: 'right', marginTop: 4 }}>{message.length}/2000</div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 24, alignItems: 'start' }}>
              {/* saved contacts */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={groupLabel}>Vendor contacts</span>
                  {contacts.length > 1 && (
                    <button onClick={() => setPicked(picked.size === contacts.length ? new Set() : new Set(contacts.map(c => c.id)))}
                      style={{ background: 'none', border: 'none', color: accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                      {picked.size === contacts.length ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>
                {!contactsLoaded ? (
                  <div style={{ height: 64, borderRadius: 10, background: surface2, animation: 'qShimmer 1.2s infinite' }} />
                ) : contacts.length === 0 ? (
                  <div style={{ border: `1px dashed ${line}`, borderRadius: 12, padding: '24px 18px', textAlign: 'center', color: slate, fontSize: 13.5, lineHeight: 1.6 }}>
                    No contacts with an email are saved for this vendor yet.
                    <br />Add someone on the right -- you can save them as a contact too.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {contacts.map(c => {
                      const on = picked.has(c.id);
                      return (
                        <button key={c.id} onClick={() => togglePick(c.id)} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', fontFamily: sans, textAlign: 'left',
                          border: `1.5px solid ${on ? accent : line}`, background: on ? selectedBg : surface,
                        }}>
                          <span style={checkbox(on)}>{on && <Icon d={CHECK} size={12} />}</span>
                          <Avatar name={c.name || c.email || '?'} size={34} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14.5, fontWeight: 600, color: ink }}>{c.name || c.email}</div>
                            <div style={{ fontSize: 13, color: slate, marginTop: 1 }}>{c.email}{c.role ? ` · ${c.role}` : ''}</div>
                          </div>
                          {c.is_primary && <span style={{ fontSize: 11.5, fontWeight: 700, color: accent, background: isDark ? '#172554' : T.blueBg, borderRadius: 99, padding: '3px 9px' }}>Primary</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* new people */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={groupLabel}>New recipients</span>
                  <button onClick={addPerson} style={{ background: 'none', border: 'none', color: accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
                    + Add person
                  </button>
                </div>
                {newPeople.length === 0 ? (
                  <button onClick={addPerson} style={{
                    width: '100%', border: `1.5px dashed ${line}`, borderRadius: 12, padding: '22px 18px', background: 'transparent', cursor: 'pointer',
                    color: slate, fontSize: 14, fontFamily: sans,
                  }}>
                    <span style={{ color: accent, fontWeight: 700 }}>+ Send to someone new</span>
                    <div style={{ fontSize: 12.5, color: muted, marginTop: 4 }}>Anyone at the vendor who isn't in the list yet</div>
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {newPeople.map(p => {
                      const bad = !!p.email.trim() && !emailOk(p.email);
                      const exists = !!p.email.trim() && knownEmails.has(p.email.trim().toLowerCase());
                      return (
                        <div key={p.key} style={{ border: `1px solid ${line}`, borderRadius: 12, padding: 14, background: surface2 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                            <div>
                              <label style={{ ...fieldLabel, fontSize: 12.5 }}>Name</label>
                              <input value={p.name} onChange={e => updatePerson(p.key, { name: e.target.value })} placeholder="Jordan Lee" style={inputStyle} />
                            </div>
                            <div>
                              <label style={{ ...fieldLabel, fontSize: 12.5 }}>Email</label>
                              <input value={p.email} onChange={e => updatePerson(p.key, { email: e.target.value })} placeholder="jordan@vendor.com"
                                style={{ ...inputStyle, borderColor: bad ? '#DC2626' : line }} />
                            </div>
                            <button onClick={() => removePerson(p.key)} aria-label="Remove" title="Remove" style={{
                              width: 40, height: 40, borderRadius: 8, border: `1px solid ${line}`, background: surface, color: slate, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}><Icon d={CLOSE} /></button>
                          </div>
                          {bad && <div style={{ fontSize: 12.5, color: '#DC2626', marginTop: 6 }}>Enter a valid email address.</div>}
                          {exists ? (
                            <div style={{ fontSize: 12.5, color: slate, marginTop: 8 }}>Already a saved contact -- tick them on the left instead.</div>
                          ) : (
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: slate, marginTop: 10, cursor: 'pointer' }}>
                              <span style={checkbox(p.save)} onClick={() => updatePerson(p.key, { save: !p.save })}>{p.save && <Icon d={CHECK} size={12} />}</span>
                              <span onClick={() => updatePerson(p.key, { save: !p.save })}>Save to this vendor's contacts{!p.name.trim() && p.save ? ' (needs a name)' : ''}</span>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderRadius: 10, background: surface2, border: `1px solid ${line}` }}>
                <span style={{ fontSize: 14, color: duplicateEmail ? '#DC2626' : ink }}>
                  {duplicateEmail
                    ? `${duplicateEmail} is listed twice.`
                    : recipients.length
                      ? <><strong>{recipients.length}</strong> recipient{recipients.length === 1 ? '' : 's'} -- each gets their own copy and answer link.</>
                      : 'Pick at least one contact or add someone new.'}
                </span>
                <span style={{ fontSize: 12.5, color: muted }}>No FluxWatch account needed on their side.</span>
              </div>
            </div>
          )}

          {step === 4 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, alignItems: 'start' }}>
              <div style={{ border: `1px solid ${line}`, borderRadius: 12, overflow: 'hidden' }}>
                {([
                  ['Questionnaire', selected ? selected.name : '—'],
                  ['Sections', `${selectedSections.size} of ${sectionGroups.length} · ${selectedQuestionCount} of ${templateItems.length} questions\n`
                    + sectionGroups.filter(g => selectedSections.has(g.key))
                      .map(g => g.title ? `${g.key}. ${g.title}` : (g.key ? `Section ${g.key}` : 'General')).join('\n')],
                  ['Due date', dueDate ? `${fmtDate(dueDate)} (in ${daysUntil(dueDate)} days)` : 'No due date'],
                  ...(dueDate ? [['Reminder', reminderDays === 'default' ? 'Default (every 3 days)'
                    : reminderDays === 'none' ? 'No reminder' : `Every ${reminderDays} day${reminderDays === '1' ? '' : 's'}`] as [string, string]] : []),
                  ['Note', message || '—'],
                ] as [string, string][]).map(([k, v], i) => (
                  <div key={k} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12, padding: '14px 18px', borderTop: i ? `1px solid ${line}` : 'none', background: i % 2 ? 'transparent' : surface2 }}>
                    <span style={{ fontSize: 14, color: slate }}>{k}</span>
                    <span style={{ fontSize: 14.5, color: ink, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ ...groupLabel, marginBottom: 12 }}>Recipients ({recipients.length})</div>
                <div style={{ border: `1px solid ${line}`, borderRadius: 12, overflow: 'hidden' }}>
                  {recipients.map((r, i) => (
                    <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i ? `1px solid ${line}` : 'none' }}>
                      <Avatar name={r.name || r.email} size={32} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: ink }}>{r.name || r.email.split('@')[0]}</div>
                        <div style={{ fontSize: 13, color: slate }}>{r.email}</div>
                      </div>
                      {r.isNew && (
                        <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 99, padding: '3px 9px', color: r.save && r.name ? '#16A34A' : slate, background: r.save && r.name ? `#16A34A${isDark ? '22' : '14'}` : surface2 }}>
                          {r.save && r.name ? 'New · will be saved' : 'New'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 4 && sendError && <div style={{ fontSize: 13.5, color: '#DC2626', marginTop: 16, lineHeight: 1.5 }}>{sendError}</div>}
          {step === 4 && aiBlocked && <div style={{ fontSize: 13.5, color: '#DC2626', marginTop: 16, lineHeight: 1.5 }}>{aiStatus.data?.reason || 'An AI provider is required before questionnaires can be sent.'}</div>}
        </div>

        {/* footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '16px 32px', borderTop: `1px solid ${line}`, background: surface2 }}>
          <button onClick={step === 0 ? onClose : () => setStep(s => s - 1)} disabled={sending} style={{ ...topBtn(false, isDark), padding: '9px 18px', fontSize: 14 }}>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button disabled={!canNext} onClick={() => setStep(s => s + 1)}
              style={{ ...topBtn(true, isDark), padding: '9px 20px', fontSize: 14, cursor: canNext ? 'pointer' : 'not-allowed', opacity: canNext ? 1 : 0.45 }}>
              Continue
            </button>
          ) : (
            <button
              disabled={sending}
              onClick={() => {
                if (aiBlocked && aiStatus.data) {
                  window.dispatchEvent(new CustomEvent('fluxwatch:ai-blocked', {
                    detail: { aiMode: aiStatus.data.ai_mode, message: aiStatus.data.reason || '' },
                  }));
                  return;
                }
                handleSend();
              }}
              style={{ ...topBtn(true, isDark), padding: '9px 20px', fontSize: 14, cursor: sending ? 'wait' : 'pointer', opacity: sending || aiBlocked ? 0.7 : 1 }}
              title={aiBlocked ? (aiStatus.data?.reason || undefined) : undefined}
            >
              <Icon d={SEND} /> {sending ? 'Sending…' : `Send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Response drawer: timeline, progress and every answer, grouped by section ── */
function ResponseDrawer({ row, isDark, canReview, vendorId, onClose, onReviewed }: {
  row: QuestionnaireResponse; isDark: boolean; canReview: boolean; vendorId: string; onClose: () => void; onReviewed: () => void;
}) {
  const [answerSearch, setAnswerSearch] = useState('');
  const [answerFilter, setAnswerFilter] = useState<'all' | 'unanswered' | 'flagged' | 'recommendation' | 'mismatch'>('all');
  const [reviewError, setReviewError] = useState('');
  // Closing plays the slide-out animation before actually unmounting (onAnimationEnd below calls
  // the real onClose) -- a plain CSS keyframe, not a rAF-driven transition, since rAF pauses in a
  // hidden pane/tab and would leave the drawer stuck mid-close (same reasoning as the Vendors
  // page's own slide panel).
  const [closing, setClosing] = useState(false);
  const requestClose = () => setClosing(true);
  const [commentError, setCommentError] = useState<Record<string, string>>({});
  const [postingComment, setPostingComment] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const answersQueryKey = ['questionnaire-responses', 'detail', row.id, 'answers'];
  const answersQuery = useQuery({
    queryKey: answersQueryKey,
    queryFn: async () => (await api.get(`/v1/questionnaire-responses/${row.id}/answers`)).data as {
      response: QuestionnaireResponse | null; questions?: AnswerQuestion[];
      disagreement_counts?: { total: number; high: number; medium: number; low: number };
    },
  });
  const details = answersQuery.data?.response ?? null;
  const questions = answersQuery.data?.questions || NO_QUESTIONS;
  const loading = !answersQuery.data && (answersQuery.isPending || answersQuery.isFetching);
  const loadError = !answersQuery.data && answersQuery.isError
    ? apiError(answersQuery.error, 'Could not load the answers for this questionnaire.') : '';

  const reviewMutation = useMutation({
    mutationFn: () => api.patch(`/v1/questionnaire-responses/${row.id}/review`),
    meta: { invalidates: [['questionnaire-responses']] },
  });
  const reviewing = reviewMutation.isPending;

  const [overviewError, setOverviewError] = useState('');
  const regenerateOverviewMutation = useMutation({
    mutationFn: () => api.post(`/v1/questionnaire-responses/${row.id}/overview/regenerate`),
    onSuccess: data => {
      setOverviewError('');
      queryClient.setQueryData(answersQueryKey, (prev: typeof answersQuery.data) => prev && {
        ...prev, response: data.data,
      });
    },
    onError: err => setOverviewError(apiError(err, 'Could not generate an overview. Try again.')),
  });

  const postComment = async (itemId: string, body: string) => {
    setPostingComment(itemId);
    setCommentError(prev => ({ ...prev, [itemId]: '' }));
    try {
      const { data } = await api.post(`/v1/questionnaire-responses/${row.id}/comments`, { item_id: itemId, body });
      queryClient.setQueryData(answersQueryKey, (prev: typeof answersQuery.data) => prev && {
        ...prev,
        questions: (prev.questions || []).map(q => (q.id === itemId ? { ...q, comments: data.comments?.[itemId] || [] } : q)),
      });
    } catch (err) {
      setCommentError(prev => ({ ...prev, [itemId]: apiError(err, 'Could not post that comment. Try again.') }));
    } finally {
      setPostingComment(null);
    }
  };

  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const accent = isDark ? '#3B82F6' : T.blue;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = row.status;
  const meta = STATUS_META[status] || STATUS_META.sent;
  const message = details?.message || row.message;

  const isFlagged = (q: AnswerQuestion) => {
    const t = answerTone(q.answer_value);
    return t === '#DC2626' || t === '#D97706';
  };
  const hasRecommendation = (q: AnswerQuestion) => (q.comments || []).length > 0;

  const filterCounts = useMemo(() => ({
    all: questions.length,
    unanswered: questions.filter(q => !q.answer_value).length,
    flagged: questions.filter(isFlagged).length,
    recommendation: questions.filter(hasRecommendation).length,
    mismatch: questions.filter(q => !!q.disagreement).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [questions]);

  const sections = useMemo(() => {
    const needle = answerSearch.trim().toLowerCase();
    const groups = new Map<string, { q: AnswerQuestion; n: number }[]>();
    questions.forEach((q, i) => {
      if (answerFilter === 'unanswered' && q.answer_value) return;
      if (answerFilter === 'flagged' && !isFlagged(q)) return;
      if (answerFilter === 'recommendation' && !hasRecommendation(q)) return;
      if (answerFilter === 'mismatch' && !q.disagreement) return;
      if (needle && !(q.question_text.toLowerCase().includes(needle) || (q.answer_value || '').toLowerCase().includes(needle))) return;
      const key = q.section || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ q, n: i + 1 });
    });
    return Array.from(groups.entries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, answerSearch, answerFilter]);

  const answered = questions.filter(q => q.answer_value).length;

  const handleReview = async () => {
    setReviewError('');
    try {
      await reviewMutation.mutateAsync();
      onReviewed();
    } catch (err) {
      setReviewError(apiError(err, 'Could not mark this questionnaire reviewed.'));
    }
  };

  const sectionLabel: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: slate };

  return (
    <>
      <div onClick={requestClose} style={{ position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,.35)', zIndex: 1200 }} />
      <aside
        onAnimationEnd={() => { if (closing) onClose(); }}
        style={{
          position: 'fixed', top: 64, right: 0, bottom: 0, width: 'max(680px, 50vw)', maxWidth: '100%', zIndex: 1201, background: surface,
          borderLeft: `1px solid ${line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
          animation: closing ? 'qSlideOut .32s ease-in forwards' : 'qSlideIn .32s ease-out',
        }}
      >
        {/* header */}
        <div style={{ padding: '22px 26px 20px', borderBottom: `1px solid ${line}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
              <span style={{ width: 44, height: 44, borderRadius: 11, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#172554' : T.blueBg, color: accent }}>
                <Icon d={DOC} size={20} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: ink }}>{row.questionnaire_name}</div>
                <div style={{ fontSize: 13.5, color: slate, marginTop: 3 }}>
                  Sent to {row.recipient_name ? `${row.recipient_name} · ` : ''}{row.recipient_email}
                </div>
              </div>
            </div>
            <button onClick={requestClose} aria-label="Close" title="Close (Esc)" style={{
              background: surface2, border: `1px solid ${line}`, color: slate, cursor: 'pointer', width: 32, height: 32, borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><Icon d={CLOSE} /></button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
              color: meta.color, background: `${meta.color}${isDark ? '22' : '14'}`, border: `1px solid ${meta.color}40`,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: meta.color }} />{meta.label}
            </span>
            {isOverdue(row) && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 99, color: '#DC2626', background: `#DC2626${isDark ? '22' : '14'}`, border: '1px solid #DC262640' }}>
                Overdue since {fmtDate(row.due_date)}
              </span>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* overview -- once the vendor has submitted everything */}
          {(status === 'answered' || status === 'reviewed') && !loading && !loadError && (
            <section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                <div style={sectionLabel}>Overview</div>
                {details?.ai_overview && (
                  <button
                    onClick={() => regenerateOverviewMutation.mutate()}
                    disabled={regenerateOverviewMutation.isPending}
                    style={{ background: 'none', border: 'none', color: accent, fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: regenerateOverviewMutation.isPending ? 'wait' : 'pointer' }}
                  >
                    {regenerateOverviewMutation.isPending ? 'Regenerating…' : '↻ Regenerate'}
                  </button>
                )}
              </div>

              {filterCounts.mismatch > 0 && (
                <div style={{ fontSize: 13, color: ink, marginBottom: 12 }}>
                  <span style={{ color: '#DC2626', fontWeight: 700 }}>⚠ {filterCounts.mismatch} answer{filterCounts.mismatch === 1 ? '' : 's'}</span>
                  {' '}don't match their evidence —{' '}
                  <button
                    onClick={() => setAnswerFilter('mismatch')}
                    style={{ background: 'none', border: 'none', padding: 0, color: accent, fontWeight: 600, fontFamily: sans, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    look here first
                  </button>.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
                {[
                  ['Answered', `${filterCounts.all - filterCounts.unanswered}/${filterCounts.all}`, slate, null],
                  ['Flagged', String(filterCounts.flagged), filterCounts.flagged ? '#DC2626' : slate, null],
                  ['Evidence', String(questions.filter(q => (q.evidence || []).length > 0).length), slate, null],
                  ['Recommendations', String(filterCounts.recommendation), filterCounts.recommendation ? '#D97706' : slate, null],
                  ["Doesn't match evidence", String(filterCounts.mismatch), filterCounts.mismatch ? '#DC2626' : slate, 'mismatch'],
                ].map(([label, value, color, filterKey]) => (
                  <div
                    key={label}
                    onClick={filterKey ? () => setAnswerFilter(filterKey as typeof answerFilter) : undefined}
                    title={filterKey ? 'Jump to these answers' : undefined}
                    style={{
                      padding: '10px 12px', borderRadius: 8, background: surface2, border: `1px solid ${line}`,
                      cursor: filterKey ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ fontSize: 18, fontWeight: 800, color: color as string }}>{value}</div>
                    <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              {details?.ai_overview ? (
                <div style={{ fontSize: 13.5, color: ink, lineHeight: 1.6, background: surface2, border: `1px solid ${line}`, borderRadius: 8, padding: '12px 14px' }}>
                  {details.ai_overview}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: muted, fontStyle: 'italic' }}>
                  {regenerateOverviewMutation.isPending ? 'Generating overview…' : "An AI overview is being generated and will appear here shortly. If it doesn't, your tenant's AI provider key may need attention."}
                  {!regenerateOverviewMutation.isPending && (
                    <button
                      onClick={() => regenerateOverviewMutation.mutate()}
                      style={{ marginLeft: 8, background: 'none', border: 'none', color: accent, fontSize: 12.5, fontWeight: 600, fontFamily: sans, cursor: 'pointer', fontStyle: 'normal' }}
                    >
                      Generate now
                    </button>
                  )}
                </div>
              )}
              {overviewError && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{overviewError}</div>}
            </section>
          )}

          {/* timeline */}
          <section>
            <div style={{ ...sectionLabel, marginBottom: 16 }}>Timeline</div>
            <Stepper status={status} isDark={isDark} large dates={[row.sent_at, row.opened_at, row.answered_at, row.reviewed_at]} />
          </section>

          {/* summary */}
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              ['Completion', `${row.percent_complete}%`, `${row.answered_count} of ${row.total_items} answered`],
              ['Due date', row.due_date ? fmtDate(row.due_date) : 'None', row.due_date ? (daysUntil(row.due_date) >= 0 ? `${daysUntil(row.due_date)} days left` : `${Math.abs(daysUntil(row.due_date))} days ago`) : 'No deadline set'],
              ['Sent', fmtDate(row.sent_at), fmtRelative(row.sent_at)],
            ].map(([label, value, note]) => (
              <div key={label} style={{ border: `1px solid ${line}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: slate, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: ink, marginTop: 6 }}>{value}</div>
                <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>{note}</div>
              </div>
            ))}
          </section>

          {message && (
            <section>
              <div style={{ ...sectionLabel, marginBottom: 10 }}>Note sent with it</div>
              <div style={{ fontSize: 14, color: ink, lineHeight: 1.6, background: surface2, border: `1px solid ${line}`, borderRadius: 10, padding: '12px 14px', whiteSpace: 'pre-wrap' }}>
                {message}
              </div>
            </section>
          )}

          {/* answers */}
          <section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={sectionLabel}>Answers {!loading && !loadError && `(${answered}/${questions.length})`}</div>
              {!loading && !loadError && questions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 8, background: surface2, border: `1px solid ${line}` }}>
                    {([
                      ['all', 'All', undefined], ['unanswered', 'Unanswered', undefined],
                      ['flagged', 'Flagged', filterCounts.flagged ? '#DC2626' : undefined],
                      ['recommendation', 'Recommendations', filterCounts.recommendation ? '#D97706' : undefined],
                      ['mismatch', "Doesn't match evidence", filterCounts.mismatch ? '#DC2626' : undefined],
                    ] as [typeof answerFilter, string, string | undefined][]).map(([key, label, tone]) => (
                      <button
                        key={key}
                        onClick={() => setAnswerFilter(key)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: 12.5, fontWeight: 600, fontFamily: sans,
                          cursor: 'pointer', border: 'none', borderRadius: 6, whiteSpace: 'nowrap',
                          background: answerFilter === key ? surface : 'transparent', color: answerFilter === key ? ink : slate,
                          boxShadow: answerFilter === key ? (isDark ? '0 0 0 1px #262626' : '0 1px 2px rgba(15,23,42,.08), 0 0 0 1px #E2E8F0') : 'none',
                        }}
                      >
                        {label}
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 99, fontVariantNumeric: 'tabular-nums',
                          background: tone ? `${tone}22` : (answerFilter === key ? (isDark ? '#1f2937' : T.surface2) : (isDark ? '#1a1a1a' : '#EEF2F6')),
                          color: tone || slate,
                        }}>{filterCounts[key]}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: muted, display: 'flex' }}><Icon d={SEARCH} size={13} /></span>
                    <input value={answerSearch} onChange={e => setAnswerSearch(e.target.value)} placeholder="Search answers…" style={{
                      width: 180, boxSizing: 'border-box', padding: '7px 10px 7px 28px', fontSize: 13, fontFamily: sans, color: ink,
                      background: surface, border: `1px solid ${line}`, borderRadius: 7, outline: 'none',
                    }} />
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1, 2, 3].map(i => <div key={i} style={{ height: 70, borderRadius: 10, background: surface2, animation: 'qShimmer 1.2s infinite' }} />)}
              </div>
            ) : loadError ? (
              <div style={{ fontSize: 13.5, color: '#DC2626' }}>{loadError}</div>
            ) : status === 'sent' && answered === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 12px', border: `1px dashed ${line}`, borderRadius: 10, color: slate, fontSize: 13.5, lineHeight: 1.6 }}>
                {row.recipient_name || row.recipient_email} hasn't opened this questionnaire yet.
                <br />Answers will appear here as they're submitted.
              </div>
            ) : sections.length === 0 ? (
              <div style={{ fontSize: 13.5, color: slate, padding: '12px 0' }}>No questions match.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {sections.map(([section, items]) => (
                  <div key={section || 'none'}>
                    {section && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: ink, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        Section {section}
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: muted }}>{items.length} question{items.length === 1 ? '' : 's'}</span>
                      </div>
                    )}
                    <div style={{ border: `1px solid ${line}`, borderRadius: 10, overflow: 'hidden' }}>
                      {items.map(({ q, n }, i) => {
                        const tone = answerTone(q.answer_value);
                        return (
                          <div key={q.id} style={{ display: 'flex', gap: 12, padding: '14px 16px', borderTop: i ? `1px solid ${line}` : 'none' }}>
                            <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, color: muted, minWidth: 30, paddingTop: 2 }}>Q{n}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, color: ink, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{q.question_text}</div>
                              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {!q.answer_value ? (
                                  <span style={{ fontSize: 12.5, color: muted, fontStyle: 'italic' }}>Not answered</span>
                                ) : tone ? (
                                  <span style={{ fontSize: 12.5, fontWeight: 700, padding: '3px 10px', borderRadius: 6, color: tone, background: `${tone}${isDark ? '22' : '14'}`, border: `1px solid ${tone}40` }}>
                                    {q.answer_value}
                                  </span>
                                ) : (
                                  <div style={{ fontSize: 13.5, color: ink, background: surface2, borderLeft: `3px solid ${accent}`, borderRadius: 6, padding: '8px 12px', whiteSpace: 'pre-wrap' }}>
                                    {q.answer_value}
                                  </div>
                                )}
                                {q.answer_value && q.ai_suggested && (
                                  <span style={{
                                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6,
                                    color: '#7C3AED', background: isDark ? 'rgba(124,58,237,.15)' : '#F5F3FF',
                                    border: `1px solid ${isDark ? '#4C1D95' : '#DDD6FE'}`,
                                  }}>
                                    ✦ AI-suggested — not yet reviewed by vendor
                                  </span>
                                )}
                                {q.disagreement && (
                                  <span
                                    title={q.disagreement.reason}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
                                      color: DISAGREEMENT_META[q.disagreement.severity].color,
                                      background: `${DISAGREEMENT_META[q.disagreement.severity].color}${isDark ? '22' : '14'}`,
                                      border: `1px solid ${DISAGREEMENT_META[q.disagreement.severity].color}55`,
                                    }}
                                  >
                                    ⚠ {DISAGREEMENT_META[q.disagreement.severity].label}
                                  </span>
                                )}
                              </div>
                              {q.note && (
                                <div style={{ marginTop: 8, fontSize: 12.5, color: ink, background: surface2, borderLeft: `3px solid ${muted}`, borderRadius: 6, padding: '7px 11px', whiteSpace: 'pre-wrap' }}>
                                  <span style={{ fontWeight: 700, color: muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>Vendor's note</span>
                                  <div style={{ marginTop: 2 }}>{q.note}</div>
                                </div>
                              )}
                              {q.evidence && q.evidence.length > 0 && (
                                <div style={{ marginTop: 9, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                  {q.evidence.map(doc => (
                                    <button
                                      key={doc.id}
                                      type="button"
                                      onClick={() => downloadEvidence(vendorId, doc)}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 10px', borderRadius: 6, width: 'fit-content',
                                        fontSize: 12, fontWeight: 600, color: accent, background: isDark ? 'rgba(59,130,246,.12)' : T.blueBg,
                                        border: `1px solid ${isDark ? '#1e3a8a' : T.blueBdr}`, cursor: 'pointer', fontFamily: sans,
                                      }}
                                    >
                                      📎 {doc.file_name}<span style={{ color: muted, fontWeight: 400 }}>{doc.document_type ? ` · ${doc.document_type}` : ''}{doc.size_bytes ? ` · ${fmtSize(doc.size_bytes)}` : ''}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {q.verification && (
                                <div
                                  title={q.verification.rationale || undefined}
                                  style={{
                                    marginTop: 7, display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content',
                                    fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 6,
                                    color: VERIFICATION_META[q.verification.verdict].color,
                                    background: `${VERIFICATION_META[q.verification.verdict].color}${isDark ? '1c' : '12'}`,
                                    border: `1px solid ${VERIFICATION_META[q.verification.verdict].color}40`,
                                  }}
                                >
                                  <span>{VERIFICATION_META[q.verification.verdict].icon}</span>
                                  {VERIFICATION_META[q.verification.verdict].label}
                                  {typeof q.verification.confidence === 'number' && q.verification.confidence > 0 && (
                                    <span style={{ opacity: 0.75 }}>({q.verification.confidence}%)</span>
                                  )}
                                </div>
                              )}
                              <TenantCommentThread
                                thread={q.comments || []}
                                error={commentError[q.id]}
                                posting={postingComment === q.id}
                                isDark={isDark}
                                onPost={body => postComment(q.id, body)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* footer */}
        {canReview && status === 'answered' && (
          <div style={{ padding: '14px 26px', borderTop: `1px solid ${line}`, background: surface2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 13, color: reviewError ? '#DC2626' : slate }}>
              {reviewError || 'All answers are in. Mark it reviewed once you\'ve checked them.'}
            </span>
            <button disabled={reviewing} onClick={handleReview} style={{ ...topBtn(true, isDark), cursor: reviewing ? 'wait' : 'pointer', opacity: reviewing ? 0.7 : 1, flexShrink: 0 }}>
              <Icon d={CHECK} /> {reviewing ? 'Marking…' : 'Mark reviewed'}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

const COMMENT_ICON = 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z';
const commentTimeAgo = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// One-way: a reviewer's finding/recommendation on a "No"/"Not Applicable" answer, shown read-only
// on the vendor's side (AnswerQuestionnaire.tsx's FindingsPanel) -- the vendor never replies here.
// Collapsed unless something's already there, same as the evidence panel.
function TenantCommentThread({ thread, error, posting, isDark, onPost }: {
  thread: Comment[]; error?: string; posting: boolean; isDark: boolean; onPost: (body: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const open = expanded || thread.length > 0;

  const ink = isDark ? '#e5e7eb' : T.ink;
  const muted = isDark ? '#64748b' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;

  const submit = () => {
    const text = draft.trim();
    if (!text || posting) return;
    onPost(text);
    setDraft('');
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        style={{ marginTop: 9, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, color: muted, fontSize: 11.5, fontWeight: 600, fontFamily: sans, cursor: 'pointer' }}
      >
        <Icon d={COMMENT_ICON} size={12} /> Add finding / recommendation
      </button>
    );
  }

  return (
    <div style={{ marginTop: 10, padding: '11px 13px', borderRadius: 7, border: `1px solid ${line}`, background: surface2 }}>
      {thread.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 10 }}>
          {thread.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 8 }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9.5, fontWeight: 700, color: isDark ? '#60A5FA' : T.blue,
                background: isDark ? 'rgba(59,130,246,.14)' : T.blueBg,
              }}>{(c.author_name || '?').slice(0, 1).toUpperCase()}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: ink }}>{c.author_name || 'Unknown'}</span>
                  <span style={{ fontSize: 10, color: muted }}>you · {commentTimeAgo(c.created_at)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.5, marginTop: 1, whiteSpace: 'pre-wrap' }}>{c.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          placeholder="Describe the finding and the recommended fix…"
          rows={2}
          style={{ flex: 1, boxSizing: 'border-box', padding: '7px 9px', fontSize: 12.5, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 6, outline: 'none', resize: 'vertical' }}
        />
        <button type="button" disabled={posting || !draft.trim()} onClick={submit} style={{
          flexShrink: 0, padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: sans,
          border: 'none', background: isDark ? '#3B82F6' : T.ink, color: '#fff', cursor: posting || !draft.trim() ? 'default' : 'pointer', opacity: posting || !draft.trim() ? 0.5 : 1,
        }}>{posting ? '…' : 'Post'}</button>
      </div>
      {error && <div style={{ fontSize: 11.5, color: isDark ? '#F87171' : T.red, marginTop: 6 }}>{error}</div>}
    </div>
  );
}
